import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { AssetPropertiesDialogProps, MetadataFieldDef } from '../AssetExplorer.types';
import { useAssetExplorerApi } from '../AssetExplorerConfig';
import { MetadataFieldInput } from './MetadataFieldInput';

const emptyValueFor = (field: MetadataFieldDef): unknown => {
    if (field.type === 'TAGS' || field.type === 'CHECKBOX' || (field.type === 'DROPDOWN' && field.multiple)) {
        return [];
    }
    return '';
};

export const AssetPropertiesDialog: React.FC<AssetPropertiesDialogProps> = ({
    open,
    assetIds,
    onClose,
    onSaved,
}) => {
    const { getAssetProperties, saveAssetProperties, saveAssetPropertiesBulk } = useAssetExplorerApi();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [assetName, setAssetName] = useState('');
    const [fields, setFields] = useState<MetadataFieldDef[]>([]);
    const [values, setValues] = useState<Record<string, unknown>>({});
    const [inheritedFrom, setInheritedFrom] = useState('');
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

    const firstAssetId = assetIds[0] || '';
    const isBulk = assetIds.length > 1;

    useEffect(() => {
        if (!open || !firstAssetId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            setMergeDialogOpen(false);
            try {
                const data = await getAssetProperties(firstAssetId);
                if (cancelled) return;
                setAssetName(data.name);
                const schemaFields = data.effectiveSchema?.fields || [];
                setFields(schemaFields);
                const next: Record<string, unknown> = {};
                schemaFields.forEach((field) => {
                    const stored = data.customFields?.find((row) => row.fieldKey === field.fieldKey);
                    next[field.fieldKey] = stored ? stored.value : emptyValueFor(field);
                });
                setValues(next);
                const ancestor = data.effectiveSchema.inheritedFromTitle || data.effectiveSchema.inheritedFromName;
                setInheritedFrom(ancestor);
            } catch (err: any) {
                if (!cancelled) setError(err.message || 'Failed to load asset properties');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, firstAssetId]);

    const handleClose = () => {
        if (saving) return;
        setMergeDialogOpen(false);
        onClose();
    };

    const payloadFields = () => fields.map((field) => ({ fieldKey: field.fieldKey, value: values[field.fieldKey] }));

    const persist = async (mergeMode?: 'overwrite' | 'append') => {
        setSaving(true);
        setError(null);
        try {
            if (isBulk) {
                await saveAssetPropertiesBulk(assetIds, payloadFields(), mergeMode || 'overwrite');
            } else {
                await saveAssetProperties(firstAssetId, payloadFields());
            }
            setMergeDialogOpen(false);
            onSaved();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save asset properties');
            setMergeDialogOpen(false);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (isBulk) {
            setMergeDialogOpen(true);
            return;
        }
        await persist();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsOutlinedIcon color="secondary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isBulk ? `Asset Properties (${assetIds.length} selected)` : 'Asset Properties'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress color="secondary" />
                        </Box>
                    ) : (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {isBulk
                                    ? `Values will be applied to ${assetIds.length} assets`
                                    : assetName}
                                {inheritedFrom ? ` · Schema from ${inheritedFrom}` : ''}
                            </Typography>
                            {fields.length === 0 ? (
                                <Alert severity="info">
                                    No metadata schema is defined for this folder. Open the folder Properties to add
                                    fields, or inherit a parent schema.
                                </Alert>
                            ) : (
                                fields.map((field) => (
                                    <MetadataFieldInput
                                        key={field.fieldKey}
                                        field={field}
                                        value={values[field.fieldKey]}
                                        onChange={(key, next) => setValues((prev) => ({ ...prev, [key]: next }))}
                                        disabled={saving}
                                    />
                                ))
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        disabled={saving}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSave}
                        disabled={saving || loading || fields.length === 0}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SettingsOutlinedIcon />}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={mergeDialogOpen}
                onClose={() => !saving && setMergeDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Apply metadata to {assetIds.length} assets
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Overwrite replaces each asset’s schema fields with these values. Append merges into existing
                        values (tags and checkboxes are combined; text is concatenated).
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => setMergeDialogOpen(false)}
                        disabled={saving}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={saving}
                        onClick={() => persist('append')}
                    >
                        Append the values
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={saving}
                        onClick={() => persist('overwrite')}
                    >
                        Overwrite the values
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AssetPropertiesDialog;
